import { useState, useCallback } from 'react';
import { CSVImporter, type ImportResult } from '@importcsv/react';
import { toast } from 'sonner';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';
import {
  ideaImportColumns,
  ideaImportSchema,
  type IdeaImportRow,
} from '@/lib/import-schemas';
import { importCsvTheme } from '@/lib/import-theme';

interface ImportIdeasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ImportIdeasDialog({
  open,
  onOpenChange,
  onSuccess,
}: ImportIdeasDialogProps) {
  const { workspace, membership } = useWorkspace();
  const [importing, setImporting] = useState(false);

  const handleComplete = useCallback(
    async (result: ImportResult) => {
      if (!workspace || !membership) {
        toast.error('Workspace not loaded');
        return;
      }
      const rawRows = result.rows || [];
      const rows: IdeaImportRow[] = [];
      for (const raw of rawRows) {
        const parsed = ideaImportSchema.safeParse(raw);
        if (parsed.success) rows.push(parsed.data);
        else {
          toast.error(`Row validation failed: ${parsed.error.errors[0]?.message || 'Unknown error'}`);
          return;
        }
      }
      if (rows.length === 0) {
        toast.info('No valid rows to import');
        onOpenChange(false);
        return;
      }
      setImporting(true);
      const BATCH_SIZE = 50;
      let successCount = 0;
      const errors: string[] = [];
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const toInsert = batch.map((row) => ({
          workspace_id: workspace.id,
          title: row.title,
          description: row.description || null,
          status: row.status || 'New',
          priority: row.priority ?? 3,
          effort: row.effort ?? 3,
          impact: row.impact ?? 3,
          reach: row.reach ?? null,
          confidence: row.confidence ?? null,
          time_criticality: row.time_criticality ?? null,
          risk_reduction: row.risk_reduction ?? null,
          category: row.category || null,
          assigned_to: row.assigned_to || null,
          target_quarter: row.target_quarter || null,
          created_by: membership.user_id,
        }));
        const { data, error } = await supabase
          .from('ideas')
          .insert(toInsert)
          .select('id');
        if (error) {
          errors.push(error.message);
        } else {
          successCount += data?.length ?? 0;
        }
      }
      setImporting(false);
      if (successCount > 0) {
        toast.success(`Imported ${successCount} idea(s)`, {
          description: errors.length
            ? `${errors.length} batch(es) had errors: ${errors[0]}`
            : undefined,
        });
        onSuccess?.();
        onOpenChange(false);
      } else {
        toast.error('Import failed', {
          description: errors[0] || 'Could not insert ideas',
        });
      }
    },
    [workspace, membership, onOpenChange, onSuccess]
  );

  if (!open) return null;

  return (
    <CSVImporter
      columns={ideaImportColumns}
      modalIsOpen={open}
      modalOnCloseTriggered={() => onOpenChange(false)}
      onComplete={handleComplete}
      isModal
      primaryColor="#245F73"
      customStyles={importCsvTheme}
    />
  );
}
